import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { WordPressSiteCard } from '../../wordpress-site-card';
import { WordPressSiteForm } from '../../wordpress-site-form';
import {
  buildSitesPayload,
  createEmptySiteState,
  validateWordPressSites,
} from '../../wordpress-source-utils';
import type {
  WordPressSiteFormState,
  WordPressSiteFormErrors,
} from '../../wordpress-site-form';
import type { CollectionSourceConfig } from '@/types/source-types';

/**
 * Rebuild source_config with one site's edits applied, preserving the others.
 *
 * Carries `ssm_parameter_arn` forward when the password is unchanged:
 * buildSitesPayload can't re-emit it (it isn't in form state), so without this
 * a one-site edit wipes stored credentials.
 */
export function buildSingleSiteUpdate(
  sourceConfig: CollectionSourceConfig | null | undefined,
  siteId: string,
  draft: WordPressSiteFormState,
): CollectionSourceConfig {
  const rebuiltTarget = buildSitesPayload([draft]).wordpress_sites?.[0];
  const existingSites = sourceConfig?.wordpress_sites ?? [];

  const nextSites = existingSites.map((s) => {
    if (s.site_id !== siteId || !rebuiltTarget) return s;

    const merged = { ...s, ...rebuiltTarget };

    // The inline form owns user_content_field, so mirror the draft exactly —
    // buildSitesPayload omits the key when empty, which would otherwise leave
    // the stored value in place and make clearing the field a no-op.
    merged.user_content_field = draft.userContentField.trim() || null;

    // Content filters are edited via a separate modal, not this form, so keep
    // the stored rules verbatim; a stale draft snapshot must never clobber
    // filters saved after the draft was opened.
    merged.wordpress_exclusion_rules = s.wordpress_exclusion_rules ?? null;

    // Reconcile auth carefully across three cases:
    if (!draft.useAuth) {
      // User turned auth off → clear stored creds intentionally.
      delete merged.auth;
    } else if (draft.isPasswordDirty && draft.password) {
      // New password entered → let the backend re-store it (ARN regenerated).
      merged.auth = rebuiltTarget.auth;
    } else {
      // Username / other edits, password unchanged → keep the stored ARN.
      merged.auth = {
        ...s.auth,
        ...rebuiltTarget.auth,
        ...(s.auth?.ssm_parameter_arn
          ? {
              ssm_parameter_arn: s.auth.ssm_parameter_arn,
              has_credentials: true,
            }
          : {}),
      };
    }
    return merged;
  });

  return {
    ...(sourceConfig ?? {}),
    wordpress_sites: nextSites,
  };
}

/**
 * Toggle collection-level reconcile without touching any site config. Preserves
 * the stored wordpress_sites verbatim (incl. every credential ARN).
 */
export function buildReconcileUpdate(
  sourceConfig: CollectionSourceConfig | null | undefined,
  reconcileEnabled: boolean,
): CollectionSourceConfig {
  return {
    ...(sourceConfig ?? {}),
    wordpress_sites: sourceConfig?.wordpress_sites ?? [],
    reconcile_enabled: reconcileEnabled,
  };
}

/**
 * Append a brand-new site to the stored config, preserving all existing sites
 * verbatim. The new site is serialized through buildSitesPayload (it has no
 * stored ARN to preserve — a fresh password, if any, goes to the backend).
 */
export function buildNewSiteUpdate(
  sourceConfig: CollectionSourceConfig | null | undefined,
  draft: WordPressSiteFormState,
): CollectionSourceConfig {
  const rebuilt = buildSitesPayload([draft]).wordpress_sites?.[0];
  const existingSites = sourceConfig?.wordpress_sites ?? [];
  return {
    ...(sourceConfig ?? {}),
    wordpress_sites: rebuilt ? [...existingSites, rebuilt] : existingSites,
  };
}

/**
 * Remove a single site from the stored config, preserving every other site
 * verbatim (including credential ARNs). Returns the rebuilt source_config with
 * the target site dropped from wordpress_sites.
 */
export function buildRemoveSiteUpdate(
  sourceConfig: CollectionSourceConfig | null | undefined,
  siteId: string,
): CollectionSourceConfig {
  const existingSites = sourceConfig?.wordpress_sites ?? [];
  return {
    ...(sourceConfig ?? {}),
    wordpress_sites: existingSites.filter((s) => s.site_id !== siteId),
  };
}

interface WpSiteInlineEditorProps {
  /** Sites derived from the stored collection config (mapConfigToSitesState). */
  sites: WordPressSiteFormState[];
  /** The collection's current stored source_config (for preserve-on-save). */
  sourceConfig: CollectionSourceConfig | null | undefined;
  /**
   * Persist a single site. Receives the full rebuilt source_config with only
   * the target site changed; all other sites are carried over untouched so a
   * one-site edit never rewrites (or wipes) sibling site auth/config.
   */
  onSaveSite: (nextConfig: CollectionSourceConfig) => Promise<void>;
  /**
   * Request removal of a site by id. The parent owns the confirmation dialog
   * and rebuilds the removal payload from the latest config at confirm time.
   */
  onRemoveSite: (siteId: string) => void;
  /** Per-site extra rendered in the collapsed summary line (e.g. last-indexed). */
  renderSummaryExtra?: (site: WordPressSiteFormState) => ReactNode;
  /** Per-site extra rendered in the expanded body (e.g. content-filters button). */
  renderBodyExtra?: (site: WordPressSiteFormState) => ReactNode;
  /** Current stored reconcile flag (for the WordPress Settings toggle). */
  reconcileEnabled: boolean;
}

// Saves one site at a time, avoiding the whole-array rewrite the edit modal
// performs -- which is what dropped per-site SSM credential ARNs.
export const WpSiteInlineEditor: FC<WpSiteInlineEditorProps> = ({
  sites,
  sourceConfig,
  onSaveSite,
  onRemoveSite,
  renderSummaryExtra,
  renderBodyExtra,
  reconcileEnabled,
}) => {
  const [expandedSiteId, setExpandedSiteId] = useState<string | null>(null);
  // Draft edits keyed by siteId. Absent → the card shows the stored values.
  const [drafts, setDrafts] = useState<Record<string, WordPressSiteFormState>>(
    {},
  );
  const [savingSiteId, setSavingSiteId] = useState<string | null>(null);
  const [savingReconcile, setSavingReconcile] = useState(false);
  // Draft for a brand-new site being added inline (null when not adding).
  const [newSiteDraft, setNewSiteDraft] =
    useState<WordPressSiteFormState | null>(null);
  const [savingNewSite, setSavingNewSite] = useState(false);

  // Drop stale drafts if the underlying site list changes (e.g. after a save
  // refetch) so we never show an edit against a site that no longer exists.
  useEffect(() => {
    setDrafts((prev) => {
      const validIds = new Set(sites.map((s) => s.siteId));
      const next: Record<string, WordPressSiteFormState> = {};
      for (const [id, draft] of Object.entries(prev)) {
        if (validIds.has(id)) next[id] = draft;
      }
      return next;
    });
  }, [sites]);

  const siteById = useMemo(() => {
    const map: Record<string, WordPressSiteFormState> = {};
    for (const s of sites) map[s.siteId] = s;
    return map;
  }, [sites]);

  const handleSave = async (siteId: string) => {
    const draft = drafts[siteId];
    if (!draft) return;

    const nextConfig = buildSingleSiteUpdate(sourceConfig, siteId, draft);

    setSavingSiteId(siteId);
    try {
      await onSaveSite(nextConfig);
      // Clear the draft on success; the refetched stored values take over.
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[siteId];
        return next;
      });
    } catch {
      // onSaveSite already surfaced the error; keep the draft so the user can
      // retry without re-entering their edits.
    } finally {
      setSavingSiteId(null);
    }
  };

  const handleToggleReconcile = async (next: boolean) => {
    setSavingReconcile(true);
    try {
      await onSaveSite(buildReconcileUpdate(sourceConfig, next));
    } catch {
      // onSaveSite already surfaced the error.
    } finally {
      setSavingReconcile(false);
    }
  };

  const handleSaveNewSite = async () => {
    if (!newSiteDraft) return;
    setSavingNewSite(true);
    try {
      await onSaveSite(buildNewSiteUpdate(sourceConfig, newSiteDraft));
      setNewSiteDraft(null);
    } catch {
      // onSaveSite already surfaced the error; keep the draft for retry.
    } finally {
      setSavingNewSite(false);
    }
  };

  const isSaving = savingSiteId !== null || savingReconcile || savingNewSite;

  const newSiteErrors = newSiteDraft
    ? validateWordPressSites([newSiteDraft], { isEditing: false })[
        newSiteDraft.siteId
      ] || {}
    : {};

  return (
    <div className='mt-2 space-y-2'>
      {/* Collection-level controls. This editor renders inside the dedicated
          "WordPress Settings" tab, so these sit as the first items directly —
          no nested settings section. */}
      <label className='flex items-start gap-2 rounded-md border border-border px-3 py-2 cursor-pointer'>
        <Checkbox
          checked={reconcileEnabled}
          disabled={isSaving}
          onCheckedChange={(checked) =>
            void handleToggleReconcile(checked === true)
          }
          className='mt-0.5'
        />
        <span className='space-y-0.5'>
          <span className='block text-sm font-medium'>
            Enable nightly deletion reconcile
          </span>
          <span className='block text-xs text-muted-foreground'>
            Sweeps this collection against live WordPress content each night and
            deletes points for removed posts.
          </span>
        </span>
      </label>
      {newSiteDraft ? (
        <div className='rounded-md border border-border'>
          <div className='border-b border-border px-3 py-2 text-sm font-medium'>
            New WordPress site
          </div>
          <div className='p-4'>
            <WordPressSiteForm
              site={newSiteDraft}
              onChange={(patch) =>
                setNewSiteDraft((prev) => (prev ? { ...prev, ...patch } : prev))
              }
              errors={newSiteErrors}
              disabled={savingNewSite}
            />
            <div className='mt-4 flex items-center justify-end gap-2 border-t border-border pt-4'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={() => setNewSiteDraft(null)}
                disabled={savingNewSite}
              >
                Cancel
              </Button>
              <Button
                type='button'
                size='sm'
                onClick={() => void handleSaveNewSite()}
                disabled={isSaving || Object.keys(newSiteErrors).length > 0}
              >
                {savingNewSite ? (
                  <>
                    <Loader2 className='size-4 mr-2 animate-spin' />
                    Adding...
                  </>
                ) : (
                  'Add site'
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Button
          type='button'
          variant='outline'
          className='w-full border-dashed'
          disabled={isSaving}
          onClick={() => setNewSiteDraft(createEmptySiteState())}
        >
          <Plus className='size-4 mr-2' />
          Add another WordPress site
        </Button>
      )}

      {sites.map((storedSite) => {
        const siteId = storedSite.siteId;
        const draft = drafts[siteId];
        const displaySite = draft ?? storedSite;
        const isDirty = Boolean(draft);
        const errors: Record<string, WordPressSiteFormErrors> = draft
          ? validateWordPressSites([draft], { isEditing: true })
          : {};
        const siteErrors = errors[siteId] || {};

        return (
          <WordPressSiteCard
            key={siteId}
            site={displaySite}
            isOpen={expandedSiteId === siteId}
            onToggle={() =>
              setExpandedSiteId(expandedSiteId === siteId ? null : siteId)
            }
            onChange={(patch) =>
              setDrafts((prev) => ({
                ...prev,
                [siteId]: { ...(prev[siteId] ?? siteById[siteId]), ...patch },
              }))
            }
            // Remove is wired to the parent (which owns the confirm dialog);
            // duplicate stays in the create/modal flow.
            onRemove={() => onRemoveSite(siteId)}
            onDuplicate={() => {}}
            showActions={!isSaving}
            showDuplicate={false}
            // Allow removing even the last site: emptying wordpress_sites is how
            // a user switches the collection source away from WordPress (the
            // edit modal only unlocks the source radio once the array is empty).
            // Deletion is confirm-gated by the parent, so no accidental wipe.
            isOnlySite={false}
            errors={siteErrors}
            hasErrors={Object.keys(siteErrors).length > 0}
            isEditing
            isDirty={isDirty}
            // Serialize saves: block every per-site save while any save is in
            // flight, so a second save can't PATCH a stale pre-refetch config
            // and roll back the first site's changes.
            disabled={isSaving && savingSiteId !== siteId}
            isSaving={savingSiteId === siteId}
            summaryExtra={renderSummaryExtra?.(storedSite)}
            bodyExtra={renderBodyExtra?.(storedSite)}
            onSave={() => void handleSave(siteId)}
            onCancel={() =>
              setDrafts((prev) => {
                const next = { ...prev };
                delete next[siteId];
                return next;
              })
            }
          />
        );
      })}
    </div>
  );
};
